import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ShowChart,
  Psychology,
  Assessment,
  AutoGraph,
  Lightbulb,
  Campaign as CampaignIcon,
  Newspaper as NewspaperIcon,
  RocketLaunch as RocketLaunchIcon,
  Radar as RadarIcon,
  FeedOutlined as FeedIcon,
  BarChart,
  Timeline,
  AccountBalance,
  BugReport,
  Schedule,
  CrisisAlert as CrisisAlertIcon,
  Insights as InsightsIcon,
  TrendingUp,
  ArrowUpward,
  WarningAmber,
  ArrowForward,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { apiService } from '../services/api';

// ── Stage color helpers ────────────────────────────────────────────────────────
const STAGE_COLORS: Record<number, string> = {
  0: '#4caf50', // Normal — green
  1: '#ff9800', // Euphoria — amber
  2: '#f44336', // Peak Excess — red
  3: '#e91e63', // Early Warning — deep pink
  4: '#9c27b0', // Cascade — purple
  5: '#607d8b', // Capitulation — blue-grey
};

function stageColor(stage: number): string {
  return STAGE_COLORS[stage] ?? '#9e9e9e';
}

// ── Feature registry ───────────────────────────────────────────────────────────
const FEATURES = [
  {
    title: 'Top Picks',
    desc: 'Consensus buys + Action Board — what to act on today across all signals',
    icon: <InsightsIcon />,
    path: '/top-picks',
    category: 'intelligence',
    hot: true,
  },
  {
    title: 'Market Valuation',
    desc: 'CAPE, Buffett Indicator, VIX, Yield Curve — collapse stage engine',
    icon: <CrisisAlertIcon />,
    path: '/market-valuation',
    category: 'intelligence',
    hot: true,
  },
  {
    title: 'News Signals',
    desc: 'LLM-scored news direction per ticker — bullish/bearish sentiment radar',
    icon: <CampaignIcon />,
    path: '/news-signals',
    category: 'intelligence',
  },
  {
    title: 'Media Trending',
    desc: 'Rolling 14-day mention count from financial media and podcasts',
    icon: <NewspaperIcon />,
    path: '/media-trending',
    category: 'intelligence',
  },
  {
    title: 'Trade Journal',
    desc: 'Log trades you made — auto-enriched with signals + P&L retrospective',
    icon: <MenuBookIcon />,
    path: '/trade-journal',
    category: 'intelligence',
    hot: true,
  },
  {
    title: 'Stock Scoring',
    desc: 'Technical + liquidity + signal composite score (0–100) for any ticker',
    icon: <Assessment />,
    path: '/scoring',
    category: 'technical',
  },
  {
    title: 'Volatility Ranges',
    desc: 'Bollinger + ATR + statistical buy/sell zones for the next 10 days',
    icon: <ShowChart />,
    path: '/ranges',
    category: 'technical',
  },
  {
    title: 'Trading Signals',
    desc: 'RSI / MACD / Bollinger-based buy & sell signals with regime awareness',
    icon: <TrendingUp />,
    path: '/signals',
    category: 'technical',
  },
  {
    title: 'ML Predictions',
    desc: 'XGBoost 10-day price forecast with 95% confidence interval',
    icon: <Psychology />,
    path: '/predictions',
    category: 'technical',
  },
  {
    title: 'Stocks of Future',
    desc: 'Thematic breakout candidates — AI Health, Precision Bio, Advanced Materials…',
    icon: <RocketLaunchIcon />,
    path: '/future-stocks',
    category: 'discovery',
  },
  {
    title: 'Pattern Matching',
    desc: 'Find historical market periods that resemble the current setup',
    icon: <Timeline />,
    path: '/patterns',
    category: 'discovery',
  },
  {
    title: 'Top Movers',
    desc: 'Daily top gainers & losers with opportunity scoring',
    icon: <AutoGraph />,
    path: '/movers',
    category: 'discovery',
  },
  {
    title: 'Market Position',
    desc: 'Local + global high/low analysis across indexes and sectors',
    icon: <BarChart />,
    path: '/position',
    category: 'discovery',
  },
  {
    title: 'Israeli Startups',
    desc: 'VC deal-sourcing radar — founders, sectors, live deal signals',
    icon: <RadarIcon />,
    path: '/israeli-startups',
    category: 'research',
  },
  {
    title: 'Israeli News',
    desc: 'Daily digest of Israeli tech & startup news — LLM-analyzed',
    icon: <FeedIcon />,
    path: '/israeli-news',
    category: 'research',
  },
  {
    title: 'Recommendations',
    desc: 'Opportunity scanner — stocks meeting multiple technical + momentum criteria',
    icon: <Lightbulb />,
    path: '/recommendations',
    category: 'research',
  },
  {
    title: 'Trading',
    desc: 'IBKR order entry and position management dashboard',
    icon: <AccountBalance />,
    path: '/trading',
    category: 'research',
  },
  {
    title: 'Backtest',
    desc: 'Validate scoring logic against historical periods',
    icon: <BugReport />,
    path: '/backtest',
    category: 'research',
  },
  {
    title: 'Daily Tasks',
    desc: 'Scheduled scanning jobs — run volatility / news ingestion manually',
    icon: <Schedule />,
    path: '/daily-tasks',
    category: 'research',
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  intelligence: 'Intelligence & Signals',
  technical:    'Technical Analysis',
  discovery:    'Market Discovery',
  research:     'Research & Operations',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function TopBuyCard({ rank, item }: { rank: number; item: any }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = ['#4caf50', '#2196f3', '#ff9800'];
  const rankColor = colors[rank] ?? theme.palette.text.secondary;

  const companyLabel = item.company_name && item.company_name !== item.symbol
    ? item.company_name
    : null;
  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 120 }}>
      <Typography variant="caption" display="block" fontWeight={700}>
        {item.symbol}{companyLabel ? ` — ${companyLabel}` : ''}
      </Typography>
      {item.sector && item.sector !== '—' && (
        <Typography variant="caption" display="block" color="text.secondary">{item.sector}</Typography>
      )}
      {item.as_of && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
          Price as of {item.as_of}
        </Typography>
      )}
      {item.why && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, maxWidth: 220, whiteSpace: 'normal' }}>
          {item.why}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Card
        onClick={() => navigate('/top-picks')}
        sx={{
          cursor: 'pointer',
          border: `1px solid ${rankColor}40`,
          transition: 'transform 0.15s, box-shadow 0.15s',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
          height: '100%',
        }}
      >
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.disabled" fontWeight={700}>
              #{rank + 1} BUY
            </Typography>
            <Chip
              label={`${item.conviction ?? 0}/100`}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: rankColor + '20', color: rankColor, fontWeight: 700 }}
            />
          </Box>
          <Typography variant="h5" fontWeight={800} color={rankColor}>
            {item.symbol}
          </Typography>
          {item.company_name && item.company_name !== item.symbol && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {item.company_name}
            </Typography>
          )}
          {item.price && (
            <Typography variant="body2" color="text.secondary">
              ${item.price.toFixed(2)}
              {item.as_of && <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>({item.as_of})</Typography>}
            </Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(item.badges ?? []).slice(0, 3).map((b: string) => (
              <Chip key={b} label={b} size="small"
                sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'action.hover' }} />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Tooltip>
  );
}

function RiskGauge({ label, value, max = 1 }: { label: string; value: number | null; max?: number }) {
  const theme = useTheme();
  const pct = value != null ? Math.min(1, value / max) : 0;
  const color = pct < 0.4 ? theme.palette.success.main
    : pct < 0.7 ? theme.palette.warning.main
    : theme.palette.error.main;
  return (
    <Box sx={{ mb: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight={600} color={color}>
          {value != null ? `${Math.round(pct * 100)}th pct` : '—'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct * 100}
        sx={{
          height: 5, borderRadius: 3, bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color },
        }}
      />
    </Box>
  );
}

function MarketRiskCard({ data }: { data: any }) {
  const navigate = useNavigate();
  const cs = data?.current_stage;
  if (!cs) return null;
  const color = stageColor(cs.stage ?? 0);
  const dp = cs.danger_percentiles ?? {};
  const monthsIn = cs.months_in_stage ?? 0;
  const fo = cs.fall_odds;
  const missing = Object.values(cs.inputs ?? {}).filter((v: any) => v.status === 'missing').length;

  return (
    <Card sx={{ height: '100%', border: `1px solid ${color}40` }}>
      <CardContent>
        <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.5}>
          MARKET RISK
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: 0.2 }} />
          <Typography variant="h6" fontWeight={800} sx={{ color }}>
            Stage {cs.stage} — {cs.stage_name}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {cs.stage_label}
          {monthsIn ? ` · ${monthsIn}m in this stage` : ''}
        </Typography>

        {fo && (
          <Tooltip
            title={`${fo.summary} A stage describes today's readings; it is not a countdown to a crash.`}
            placement="top"
            arrow
          >
            <Box sx={{
              mb: 1.5, p: 0.75, borderRadius: 1,
              bgcolor: color + '15', border: `1px solid ${color}40`,
              cursor: 'help',
            }}>
              <Typography variant="caption" color="text.disabled" display="block" fontWeight={700} mb={0.25}>
                10%+ FALL WITHIN 9 MONTHS
              </Typography>
              <Typography variant="body2" fontWeight={800} sx={{ color }}>
                {Math.round(fo.fall_rate * 100)}% after months like this
              </Typography>
              <Typography variant="caption" color="text.secondary">
                vs {Math.round(fo.base_fall_rate * 100)}% for all months · {fo.verdict}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {missing > 0 && (
          <Typography variant="caption" color="warning.main" display="block" mb={1}>
            {missing} of 5 inputs unavailable{missing >= 3 ? ' — stage unreliable' : ''}
          </Typography>
        )}
        <RiskGauge label="CAPE" value={dp.cape_d} />
        <RiskGauge label="Buffett" value={dp.buffett_d} />
        <RiskGauge label="VIX complacency" value={dp.vix_d} />
        <RiskGauge label="Yield Curve" value={dp.yc_d} />
        <RiskGauge label="Credit Spread" value={dp.credit_d} />
        <Button
          size="small" endIcon={<ArrowForward />}
          onClick={() => navigate('/market-valuation')}
          sx={{ mt: 1, p: 0, minWidth: 0, fontSize: '0.7rem' }}
        >
          Full Analysis
        </Button>
      </CardContent>
    </Card>
  );
}

function FeatureCard({ feature }: { feature: typeof FEATURES[0] }) {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
        border: feature.hot ? `1px solid ${theme.palette.primary.main}40` : undefined,
      }}
    >
      <CardActionArea onClick={() => navigate(feature.path)} sx={{ height: '100%', alignItems: 'flex-start' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ color: feature.hot ? theme.palette.primary.main : 'text.secondary' }}>
              {feature.icon}
            </Box>
            {feature.hot && (
              <Chip label="HOT" size="small"
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: theme.palette.primary.main + '20',
                  color: theme.palette.primary.main, fontWeight: 700 }} />
            )}
          </Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {feature.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {feature.desc}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

function SystemStatusBar({ health }: { health: any }) {
  if (!health) return null;
  const items = [
    { label: 'DB', status: health.database === 'connected' ? 'ok' : 'err' },
    { label: 'IBKR', status: health.ibkr === 'connected' ? 'ok' : 'offline' },
    { label: 'Redis', status: health.redis === 'connected' ? 'ok' : 'offline' },
  ];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.disabled" fontWeight={600}>SYSTEM</Typography>
      {items.map(({ label, status }) => (
        <Tooltip key={label} title={`${label}: ${status}`} placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: status === 'ok' ? '#4caf50' : status === 'offline' ? '#9e9e9e' : '#f44336',
            }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [actionBoard, setActionBoard] = useState<any>(null);
  const [collapseData, setCollapseData] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [loadingBoard, setLoadingBoard] = useState(true);
  const [loadingCollapse, setLoadingCollapse] = useState(true);

  useEffect(() => {
    apiService.getActionBoard().then(setActionBoard).catch(() => {}).finally(() => setLoadingBoard(false));
    apiService.getCollapseStages().then(setCollapseData).catch(() => {}).finally(() => setLoadingCollapse(false));
    apiService.getHealth().then(setHealth).catch(() => {});
  }, []);

  const topBuys: any[] = (actionBoard?.buys ?? []).slice(0, 3);
  const categories = ['intelligence', 'technical', 'discovery', 'research'] as const;

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Trading Assistant
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Real-time signals · AI-powered analysis · Portfolio intelligence
      </Typography>

      {/* ── Hero row: top buys + market risk ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* Top 3 buys */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', bgcolor: theme.palette.success.main + '08', border: `1px solid ${theme.palette.success.main}30` }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowUpward sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={700}>Top 3 Buys Right Now</Typography>
                </Box>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/top-picks')}>
                  Full Board
                </Button>
              </Box>

              {loadingBoard ? (
                <Grid container spacing={1}>
                  {[0,1,2].map(i => <Grid item xs={12} sm={4} key={i}><Skeleton height={100} /></Grid>)}
                </Grid>
              ) : topBuys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active BUY signals — check Top Picks for the full board.
                </Typography>
              ) : (
                <Grid container spacing={1}>
                  {topBuys.map((b, i) => (
                    <Grid item xs={12} sm={4} key={b.symbol}>
                      <TopBuyCard rank={i} item={b} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Market risk */}
        <Grid item xs={12} md={5}>
          {loadingCollapse ? (
            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
          ) : collapseData ? (
            <MarketRiskCard data={collapseData} />
          ) : (
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">Market risk data unavailable</Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* ── Feature grid by category ── */}
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmber sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
        All Features
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {categories.map(cat => {
        const catFeatures = FEATURES.filter(f => f.category === cat);
        return (
          <Box key={cat} sx={{ mb: 4 }}>
            <Typography variant="overline" color="text.disabled" fontWeight={700} display="block" mb={1.5}>
              {CATEGORY_LABELS[cat]}
            </Typography>
            <Grid container spacing={2}>
              {catFeatures.map(f => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={f.path}>
                  <FeatureCard feature={f} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      <SystemStatusBar health={health} />
    </Box>
  );
};

export default Dashboard;
